import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { DEFAULT_LANGUAGE_LOCALE } from "abweb/service/locale/LocaleManager";
import { NamespaceManager } from "abweb/service/locale/NamespaceManager";
import { Translator } from "abweb/service/locale/Translator";
import { ResourceBundleLoader } from "abweb/service/locale/ResourceBundleLoader";
import { StringUtils } from "abweb/utils/StringUtils";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
//import {LogUtils} from "abweb/utils/log/LogUtils";

export class TranslatorManager {
  /**
   * @param locales locales to be loaded for current user (typically [<userPrefLocale>, en]
   */
  constructor(...locales) {
    // private static LOG = LogUtils.getLogger(__filename);
    _defineProperty(this, "namespaceMgr", void 0);
    _defineProperty(this, "_translator", void 0);
    _defineProperty(this, "moduleInfos", []);
    _defineProperty(this, "overlayModuleInfos", []);
    this.setChangedLocale(...locales);
    this.namespaceMgr = new NamespaceManager([], []);
    this._translator = new Translator(this.namespaceMgr);
  }

  /** sets the locales, and returns true iff the locales changed */
  setChangedLocale(...locales) {
    locales = this.calcLocales(...locales);
    if (ArrayUtils.isEquals(locales, this.locales)) {
      return false; // no change
    }
    this.locales = locales;
    return true;
  }
  calcLocales(...locales) {
    if (locales == null) {
      return [DEFAULT_LANGUAGE_LOCALE];
    }
    // If locales does not have 'en', always add it as it is the default locale (and matches translation team workflow)
    return locales.indexOf(DEFAULT_LANGUAGE_LOCALE) < 0 ? locales.concat(DEFAULT_LANGUAGE_LOCALE) // copy
    : locales;
  }
  get translator() {
    return this._translator;
  }

  /**
   * Async load modules and override modules into the current translator.
   * @param modulePaths Parameterized with {locale}, such as assets/{locale}/ablb.json
   * @param moduleOverridesPaths Parameterized with {locale}, such as assets/{locale}/ablb-overrides.json
   * @return {Promise<void>} returns when the async loading has completed.
   */
  // This has to be async, as it asynchronously fetches runtime-determined resource files from the server.
  // AFAIK, there is no way of converting an async method to a sync method in Typescript (short of a busy wait loop)
  loadResourceBundles(modulePaths, moduleOverridesPaths) {
    // 1. In parallel load all locale-specific resource files from server and create resource bundles
    //   1a. load module resource bundles (which in parallel loads cross product of {module X locales} files)
    //   1b. load overrides resource bundles (ditto)
    // 2. Process created resource bundles (NamespaceManager backs current translator)
    //    - nsMgr.applyOverrides(overrideRB[])
    //    - nsMgr.applyOverrides(overrideRB[])

    const that = this;
    // Validate and parse the module (overrides) paths
    const modInfos = [];
    const overlayModInfos = [];
    let modInfo;
    modulePaths.forEach(function (modPath) {
      modInfo = that.parseModPath(modPath, false);
      that.moduleInfos.push(modInfo);
      modInfos.push(modInfo);
    });
    moduleOverridesPaths.forEach(function (modOPath) {
      modInfo = that.parseModPath(modOPath, true);
      that.overlayModuleInfos.push(modInfo);
      overlayModInfos.push(modInfo);
    });

    // Fetch all resource bundles in parallel (both modules and overrides), and add to namespace mgr that
    // backs the current translator
    return this.fetchResourceBundles(modInfos, overlayModInfos);
  }

  /** Will reload all resource bundles iff the locales have changed */
  reloadResourceBundles(...locales) {
    if (!this.setChangedLocale(...locales)) {
      return Promise.resolve();
    }
    this.namespaceMgr.clearResourceBundles();
    return this.fetchResourceBundles(this.moduleInfos, this.overlayModuleInfos);
  }
  fetchResourceBundles(modInfos, overlayModInfos) {
    // Fetch all resource bundles in parallel (both modules and overrides), and add to namespace mgr that
    // backs the current translator
    const that = this;
    return Promise.all(modInfos.concat(...overlayModInfos).map(function (modInfo) {
      return ResourceBundleLoader.loadResourceBundle(modInfo.modName, modInfo.parameterizedModPath, that.locales);
    })).then(function (resBundles) {
      // separate out resource bundles for modules and overrides
      const modRBs = resBundles.slice(0, modInfos.length);
      const overrideRBs = resBundles.slice(modInfos.length);
      that.namespaceMgr.addResourceBundle(...modRBs);
      that.namespaceMgr.applyOverrides(...overrideRBs);
    });
  }
  parseModPath(modPath, isOverride) {
    StringUtils.lastIndexOfOrThrow(modPath, ResourceBundleLoader.LOCALE_PARAM_NAME);
    // modName is everything between last / and the required file suffix
    const lastDirSlash = StringUtils.lastIndexOfOrThrow(modPath, "/");
    const lastSuffix = StringUtils.lastIndexOfOrThrow(modPath, ".json");
    const modName = modPath.slice(lastDirSlash + 1, lastSuffix);
    // Enforce convention that override modules have a module name containing -overrides,
    // and that normal modules have names that do not contain -overrides
    const isOverrideModName = StringUtils.endsWith(modName, "-overrides");

    // Enforce naming consistency, and defensively detect developer error
    if (isOverrideModName !== isOverride) {
      if (isOverride) {
        throw new Error("Module '" + modPath + "' is an override module, yet it does not contain '-overrides' in the module name");
      } else {
        throw new Error("Module '" + modPath + "' is not an override module, yet it contains '-overrides' in the module name");
      }
    }
    return {
      modName: modName,
      parameterizedModPath: modPath
    };
  }
}

// internal type
//# sourceMappingURL=TranslatorManager.js.map
